// src/components/Apm/Profile/CompanyStamp.js

import { useCallback } from 'react';

import { toast } from 'react-toastify';

import { useTranslation } from 'react-i18next';

import {
  Avatar,
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  Grid,
  Typography,
} from '@mui/material';

import ApprovalIcon from '@mui/icons-material/Approval';

import { useUpdateCurrentCompanyMutation } from '../../../redux/features/companyApi';

import { useGetImageQuery } from '../../../redux/features/imageApi';

import MultipleFileUploadField, { COMPANY_IMAGE_MAX_FILE_SIZE, IMAGE_ACCEPTED_FILE_TYPES } from '../../Upload/MultipleFileUploadField';

import ButtonLoading from '../../Controls/Buttons/ButtonLoading';

export const CompanyStamp = ({ company, canEdit }) => {
  const { t } = useTranslation();

  const stampId = company?.stamp ?? null;

  const { data: stampResponse, isFetching: isStampFetching } = useGetImageQuery(stampId ? String(stampId) : '', {
    skip: !stampId,
  });

  const stamp = stampResponse?.s3_url ?? null;

  const [updateCurrentCompany, { isLoading: isUpdatingCompany }] = useUpdateCurrentCompanyMutation();

  const putCompanyStamp = useCallback(
    async (imageId) => {
      if (!canEdit) {
        toast.error(
          t('toast.only_account_manager_allowed', {
            defaultValue: 'You are not allowed to edit this company.',
          })
        );

        return null;
      }

      try {
        const updatedContext = await updateCurrentCompany({
          stamp: imageId || null,
        }).unwrap();

        toast.success(
          t('toast.stamp_successfully_updated', {
            defaultValue: 'Stamp successfully updated.',
          })
        );

        return updatedContext;
      } catch (error) {
        console.error('Company stamp update failed:', error);

        toast.error(
          t('toast.stamp_could_not_be_updated', {
            defaultValue: 'Stamp could not be updated.',
          })
        );

        return null;
      }
    },
    [canEdit, t, updateCurrentCompany]
  );

  const deleteStamp = async (event) => {
    event.preventDefault();

    if (!canEdit || !stampId) {
      return;
    }

    try {
      await updateCurrentCompany({
        stamp: null,
      }).unwrap();

      toast.success(
        t('toast.stamp_successfully_deleted', {
          defaultValue: 'Stamp successfully deleted.',
        })
      );
    } catch (error) {
      console.error('Company stamp deletion failed:', error);

      toast.error(
        t('toast.stamp_could_not_be_deleted', {
          defaultValue: 'Stamp could not be deleted.',
        })
      );
    }
  };

  return (
    <Card
      sx={{
        width: '100%',
        height: '100%',
      }}
    >
      <CardHeader
        title={<Typography variant="h6">{t('company.company_stamp')}</Typography>}
        sx={{
          backgroundColor: '#f1f1f1',
        }}
      />

      <Divider />

      <CardContent>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            justifyContent: 'center',
            height: 145,
            mt: 0.5,
          }}
        >
          {isStampFetching ? (
            <CircularProgress size={32} />
          ) : (
            <Avatar
              src={stamp || undefined}
              sx={{
                width: stamp ? 'auto' : 113.75,
                minHeight: 105,
                minWidth: 113.75,
                mb: 2,
                objectFit: 'contain',
              }}
              imgProps={{
                style: {
                  objectFit: 'contain',
                },
              }}
              variant="square"
            >
              <ApprovalIcon fontSize="large" />
            </Avatar>
          )}
        </Box>
      </CardContent>

      {canEdit && (
        <>
          <Divider />

          <CardActions>
            <MultipleFileUploadField
              variant="company-stamp"
              currentFileId={stampId}
              acceptedFileTypes={IMAGE_ACCEPTED_FILE_TYPES}
              maxFileSize={COMPANY_IMAGE_MAX_FILE_SIZE}
              disabled={isUpdatingCompany}
              onUploaded={putCompanyStamp}
            />
          </CardActions>

          <Divider />

          <CardActions
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column',
            }}
          >
            <Grid
              container
              item
              sx={{
                width: '100%',
              }}
            >
              <ButtonLoading
                type="button"
                variant="outlined"
                color="secondary"
                onClick={deleteStamp}
                loading={isUpdatingCompany}
                disabled={!stampId || isUpdatingCompany}
                fullWidth
                sx={{
                  mx: 0,
                  my: 1,
                }}
              >
                <Typography variant="button">{t('button.delete_stamp')}</Typography>
              </ButtonLoading>
            </Grid>
          </CardActions>
        </>
      )}
    </Card>
  );
};
